/**
 * InvoicesListController — page /factures.html (EPIC-013 / 13.7).
 *
 * Liste paginée des factures émises de l'org. Filtres statut / période /
 * client search, pagination 25/page, recherche libre `q` (n° ou client).
 *
 * Affiche un bandeau pédagogique au-dessus du tableau si l'API
 * `/organizations/me/legal-mention-warnings` retourne au moins un
 * warning (RCS ville manquante ou RM département manquant). Le bandeau
 * pointe vers le profil entreprise.
 *
 * Action contextuelle par ligne :
 *  - statut `draft` → lien « Éditer » vers `/facture-edition.html?id={id}`
 *  - autres statuts → lien « Voir » vers `/facture-detail.html?id={id}`
 *
 * Conventions verbatim : aucun HTML inline en JS (clone de
 * `<template>` + remplissage `data-slot` + setAttribute pour les
 * attributs `href` / `datetime`).
 */

import BaseController from "./base_controller.js";
import { api, APIError } from "/js/services/api.js";

const ORIGIN_STORAGE_KEY = "nm_filter_origin_factures";

const STATUS_LABELS = {
  draft: "brouillon",
  scheduled: "programmée",
  issued: "émise",
  sent: "envoyée",
  paid: "payée",
  partially_paid: "partiellement payée",
  overdue: "en retard",
  cancelled: "annulée",
  archived: "archivée",
};

const STATUS_PILL_MODIFIER = {
  draft: "nm-pill--indigo",
  scheduled: "nm-pill--scheduled",
  issued: "nm-pill--ochre",
  sent: "nm-pill--ochre",
  paid: "nm-pill--olive",
  partially_paid: "nm-pill--blue",
  overdue: "nm-pill--red",
  cancelled: "",
  archived: "",
};

export default class InvoicesListController extends BaseController {
  static targets = [
    "tableWrap",
    "tableBody",
    "loadingRow",
    "summary",
    "legalBanner",
    "statusFilter",
    "periodFilter",
    "clientFilter",
    "originToggleGroup",
    "originToggle",
    "pagination",
    "pageInfo",
    "pageButtons",
  ];

  static values = {
    page: { type: Number, default: 1 },
    perPage: { type: Number, default: 25 },
    q: { type: String, default: "" },
    statusFilter: { type: String, default: "" },
    fromFilter: { type: String, default: "" },
    toFilter: { type: String, default: "" },
    originFilter: { type: String, default: "all" },
  };

  connect() {
    this._debounceTimer = null;
    this._fetchSeq = 0;
    this._restoreFromQueryString();
    this._restoreOriginFromSession();
    this._loadLegalWarnings();
    this._loadImportCutover();
    this.refresh();
  }

  disconnect() {
    if (this._debounceTimer) clearTimeout(this._debounceTimer);
  }

  // ---------------------------------------------------------------------------
  // Querystring persistence
  // ---------------------------------------------------------------------------

  _restoreFromQueryString() {
    const params = new URLSearchParams(window.location.search);
    const page = Number(params.get("page"));
    if (Number.isFinite(page) && page >= 1) this.pageValue = page;
    if (params.has("status")) {
      this.statusFilterValue = params.get("status") ?? "";
      if (this.hasStatusFilterTarget) this.statusFilterTarget.value = this.statusFilterValue;
    }
    if (params.has("q")) {
      this.qValue = params.get("q") ?? "";
      if (this.hasClientFilterTarget) this.clientFilterTarget.value = this.qValue;
    }
  }

  _syncQueryString() {
    const params = new URLSearchParams();
    if (this.pageValue > 1) params.set("page", String(this.pageValue));
    if (this.statusFilterValue) params.set("status", this.statusFilterValue);
    if (this.qValue) params.set("q", this.qValue);
    const qs = params.toString();
    const newUrl = window.location.pathname + (qs ? `?${qs}` : "");
    window.history.replaceState(null, "", newUrl);
  }

  // ---------------------------------------------------------------------------
  // Filter / search / paginate
  // ---------------------------------------------------------------------------

  changeStatus(event) {
    this.statusFilterValue = event.target.value;
    this.pageValue = 1;
    this.refresh();
  }

  changePeriod(event) {
    // Convention V1 : le select Période est un raccourci préset (last_30d,
    // this_month, this_year, all) — pas de date picker. On reset
    // from/to et on laisse le backend filtrer via `q`/aucun filtre.
    // Stub volontaire — UI réversible (info).
    const value = event.target.value ?? "";
    const today = new Date();
    const fmt = (d) => d.toISOString().slice(0, 10);
    let from = "";
    let to = "";
    if (value === "last_30d") {
      const d = new Date(today);
      d.setDate(d.getDate() - 30);
      from = fmt(d);
      to = fmt(today);
    } else if (value === "this_month") {
      from = fmt(new Date(today.getFullYear(), today.getMonth(), 1));
      to = fmt(today);
    } else if (value === "this_quarter") {
      const quarterStartMonth = Math.floor(today.getMonth() / 3) * 3;
      from = fmt(new Date(today.getFullYear(), quarterStartMonth, 1));
      to = fmt(today);
    } else if (value === "this_year") {
      from = fmt(new Date(today.getFullYear(), 0, 1));
      to = fmt(today);
    } else if (value === "last_year") {
      from = fmt(new Date(today.getFullYear() - 1, 0, 1));
      to = fmt(new Date(today.getFullYear() - 1, 11, 31));
    }
    this.fromFilterValue = from;
    this.toFilterValue = to;
    this.pageValue = 1;
    this.refresh();
  }

  searchClient(event) {
    if (this._debounceTimer) clearTimeout(this._debounceTimer);
    const value = event.target.value;
    this._debounceTimer = setTimeout(() => {
      this.qValue = value;
      this.pageValue = 1;
      this.refresh();
    }, 300);
  }

  paginate(event) {
    const page = Number(event.currentTarget.dataset.page);
    if (!Number.isFinite(page) || page < 1) return;
    this.pageValue = page;
    this.refresh();
  }

  reset() {
    this.statusFilterValue = "";
    this.fromFilterValue = "";
    this.toFilterValue = "";
    this.qValue = "";
    this.originFilterValue = "all";
    this.pageValue = 1;
    if (this.hasStatusFilterTarget) this.statusFilterTarget.value = "";
    if (this.hasPeriodFilterTarget) this.periodFilterTarget.value = "";
    if (this.hasClientFilterTarget) this.clientFilterTarget.value = "";
    if (this.hasOriginToggleTarget) this.originToggleTarget.checked = true;
    this._persistOriginToSession();
    this.refresh();
  }

  changeOrigin(event) {
    this.originFilterValue = event.target.checked ? "all" : "native";
    this._persistOriginToSession();
    this.pageValue = 1;
    this.refresh();
  }

  _restoreOriginFromSession() {
    try {
      const stored = window.sessionStorage.getItem(ORIGIN_STORAGE_KEY);
      if (stored === "native" || stored === "all") {
        this.originFilterValue = stored;
        if (this.hasOriginToggleTarget) this.originToggleTarget.checked = stored === "all";
      }
    } catch {
      // sessionStorage indisponible — on garde la valeur par défaut "all".
    }
  }

  _persistOriginToSession() {
    try {
      window.sessionStorage.setItem(ORIGIN_STORAGE_KEY, this.originFilterValue);
    } catch {
      // sessionStorage indisponible — fail silencieux, l'état reste en mémoire.
    }
  }

  async _loadImportCutover() {
    if (!this.hasOriginToggleGroupTarget) return;
    try {
      const profile = await api.getOrganizationProfile();
      const cutover = profile?.importCutoverDate ?? null;
      this.originToggleGroupTarget.hidden = !cutover;
    } catch {
      this.originToggleGroupTarget.hidden = true;
    }
  }

  exportCsv() {
    // Stub V1 — non livré (annoncé dans 13.7 comme bouton mais l'API
    // d'export n'est pas dans le périmètre).
    this.showError("L'export CSV sera disponible prochainement.");
  }

  // ---------------------------------------------------------------------------
  // Bandeau RCS/RM
  // ---------------------------------------------------------------------------

  async _loadLegalWarnings() {
    if (!this.hasLegalBannerTarget) return;
    try {
      const resp = await api.getLegalMentionWarnings();
      const warnings = Array.isArray(resp?.warnings) ? resp.warnings : [];
      this.legalBannerTarget.hidden = warnings.length === 0;
    } catch {
      this.legalBannerTarget.hidden = true;
    }
  }

  // ---------------------------------------------------------------------------
  // Fetch + render
  // ---------------------------------------------------------------------------

  async refresh() {
    const seq = ++this._fetchSeq;
    if (this.hasTableWrapTarget) this.tableWrapTarget.setAttribute("aria-busy", "true");
    this._syncQueryString();

    const params = { page: this.pageValue, perPage: this.perPageValue };
    if (this.qValue) params.q = this.qValue;
    if (this.statusFilterValue) params.status = this.statusFilterValue;
    if (this.fromFilterValue) params.from = this.fromFilterValue;
    if (this.toFilterValue) params.to = this.toFilterValue;
    if (this.originFilterValue && this.originFilterValue !== "all") params.origin = this.originFilterValue;

    try {
      const response = await api.listInvoices(params);
      if (seq !== this._fetchSeq) return;
      const invoices = Array.isArray(response?.data) ? response.data : [];
      const pagination = response?.pagination ?? { page: 1, perPage: this.perPageValue, total: 0, totalPages: 1 };
      this._renderRows(invoices);
      this._renderSummary(pagination);
      this._renderPagination(pagination);
    } catch (error) {
      if (seq !== this._fetchSeq) return;
      this._renderError();
      const msg = error instanceof APIError && error.message
        ? error.message
        : "Impossible de charger les factures.";
      this.showError(msg);
    } finally {
      if (seq === this._fetchSeq && this.hasTableWrapTarget) {
        this.tableWrapTarget.setAttribute("aria-busy", "false");
      }
    }
  }

  _renderRows(invoices) {
    if (!this.hasTableBodyTarget) return;
    this._clearTableBody();

    if (invoices.length === 0) {
      this.tableBodyTarget.appendChild(this.cloneTemplate("tpl-invoice-empty"));
      return;
    }

    for (const inv of invoices) {
      const fragment = this.cloneTemplate("tpl-invoice-row");
      const detailUrl = `/facture-detail.html?id=${encodeURIComponent(inv.id)}`;
      const editUrl = `/facture-edition.html?id=${encodeURIComponent(inv.id)}`;
      // Brouillon de solde non éditable (recalculé à la volée) : on force le
      // routage vers la fiche détail même en draft. EPIC-099 / 99.49 — D6.
      const isDraft = inv.status === "draft" && inv.kind !== "settlement";

      this.fillSlots(fragment, {
        number: inv.number ?? "(brouillon)",
        clientName: inv.clientLegalName ?? "—",
        dateEmission: inv.dateEmission ? this.formatDate(inv.dateEmission) : "—",
        dateDue: inv.dateDue ? this.formatDate(inv.dateDue) : "—",
        amountExclVat: this._formatAmount(inv.amountExclVat),
        amountVat: this._formatAmount(inv.amountVat),
        amountInclVat: this._formatAmount(inv.amountInclVat),
        statusLabel: STATUS_LABELS[inv.status] ?? inv.status,
        actionText: isDraft ? "Éditer" : "Voir",
      });

      const clientLink = fragment.querySelector('[data-slot-attr-href="detailUrl"]');
      if (clientLink) clientLink.setAttribute("href", detailUrl);

      const actionLink = fragment.querySelector('[data-slot-attr-href="actionUrl"]');
      if (actionLink) {
        actionLink.setAttribute("href", isDraft ? editUrl : detailUrl);
        actionLink.setAttribute(
          "aria-label",
          isDraft
            ? `Éditer le brouillon ${inv.number ?? inv.id}`
            : `Voir la facture ${inv.number ?? inv.id}`,
        );
      }

      const dateEmissionEl = fragment.querySelector('[data-slot-attr-datetime="dateEmissionIso"]');
      if (dateEmissionEl && inv.dateEmission) {
        dateEmissionEl.setAttribute("datetime", inv.dateEmission);
      }
      const dateDueEl = fragment.querySelector('[data-slot-attr-datetime="dateDueIso"]');
      if (dateDueEl && inv.dateDue) {
        dateDueEl.setAttribute("datetime", inv.dateDue);
      }

      const pill = fragment.querySelector('[data-slot-element="statusPill"]');
      const modifier = STATUS_PILL_MODIFIER[inv.status];
      if (pill && modifier) pill.classList.add(modifier);

      const importedBadge = fragment.querySelector('[data-slot-element="importedBadge"]');
      if (importedBadge && inv.origin === "imported") importedBadge.hidden = false;

      if (inv.status === "archived" || inv.status === "cancelled") {
        const tr = fragment.querySelector("tr");
        if (tr) tr.classList.add("is-archived");
      }

      this.tableBodyTarget.appendChild(fragment);
    }
  }

  _renderSummary(pagination) {
    if (!this.hasSummaryTarget) return;
    const total = Number(pagination.total) || 0;
    this.summaryTarget.textContent = total === 0
      ? "Aucune facture"
      : `${total} facture${total > 1 ? "s" : ""}`;
  }

  _renderPagination(pagination) {
    if (!this.hasPaginationTarget || !this.hasPageButtonsTarget || !this.hasPageInfoTarget) {
      return;
    }
    const page = Number(pagination.page) || 1;
    const perPage = Number(pagination.perPage) || this.perPageValue;
    const total = Number(pagination.total) || 0;
    const totalPages = Number(pagination.totalPages) || 1;

    if (total === 0 || totalPages <= 1) {
      this.paginationTarget.hidden = true;
      return;
    }
    this.paginationTarget.hidden = false;

    const start = (page - 1) * perPage + 1;
    const end = Math.min(page * perPage, total);
    this.pageInfoTarget.textContent = `${start} — ${end} sur ${total} factures`;

    while (this.pageButtonsTarget.firstChild) {
      this.pageButtonsTarget.removeChild(this.pageButtonsTarget.firstChild);
    }
    this.pageButtonsTarget.appendChild(this._buildPageButton({
      page: page - 1, text: "‹", label: "Page précédente", disabled: page <= 1,
    }));
    for (const token of this.getPagesToShow(page, totalPages)) {
      if (token === "...") {
        this.pageButtonsTarget.appendChild(this.cloneTemplate("tpl-pagination-ellipsis"));
        continue;
      }
      this.pageButtonsTarget.appendChild(this._buildPageButton({
        page: token,
        text: String(token),
        label: `Page ${token}`,
        current: token === page,
      }));
    }
    this.pageButtonsTarget.appendChild(this._buildPageButton({
      page: page + 1, text: "›", label: "Page suivante", disabled: page >= totalPages,
    }));
  }

  _buildPageButton({ page, text, label, current = false, disabled = false }) {
    const fragment = this.cloneTemplate("tpl-pagination-page");
    const btn = fragment.querySelector("button");
    if (!btn) return fragment;
    btn.textContent = text;
    btn.setAttribute("aria-label", label);
    btn.dataset.page = String(page);
    if (disabled) btn.disabled = true;
    if (current) {
      btn.classList.add("is-current");
      btn.setAttribute("aria-current", "page");
    }
    return fragment;
  }

  _renderError() {
    if (!this.hasTableBodyTarget) return;
    this._clearTableBody();
    this.tableBodyTarget.appendChild(this.cloneTemplate("tpl-invoice-error"));
    if (this.hasPaginationTarget) this.paginationTarget.hidden = true;
    if (this.hasSummaryTarget) this.summaryTarget.textContent = "Erreur de chargement";
  }

  _clearTableBody() {
    while (this.tableBodyTarget.firstChild) {
      this.tableBodyTarget.removeChild(this.tableBodyTarget.firstChild);
    }
  }

  _formatAmount(raw) {
    if (raw === null || raw === undefined || raw === "") return "—";
    const num = Number(raw);
    if (!Number.isFinite(num)) return raw;
    return num.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
}
